<template>
  <div
    class="relative w-full h-1.5 cursor-pointer select-none"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <!-- 色相渐变背景 -->
    <div
      class="absolute inset-0 rounded-xl overflow-hidden"
      style="
        background: linear-gradient(
          to right,
          hsl(0, 100%, 50%),
          hsl(60, 100%, 50%),
          hsl(120, 100%, 50%),
          hsl(180, 100%, 50%),
          hsl(240, 100%, 50%),
          hsl(300, 100%, 50%),
          hsl(359, 100%, 50%)
        );
      "
    ></div>
    <div
      class="absolute w-full h-40 inset-0 overflow-hidden gradient-mask cursor-auto"
      :style="{
        clipPath: `inset(0 ${100 - huePercentage}% 0 0)`,
      }"
    ></div>
    <!-- 色相滑块 -->
    <div
      class="absolute w-3 h-3 border-2 glass-frosted rounded-full shadow-md -translate-y-1/2 z-10 pointer-events-none border-[var(--color-white)]"
      :style="{
        top: '50%',
        left: `${huePercentage}%`,
        transform: `translateX(-50%)`,
      }"
    ></div>
  </div>
</template>

<script setup lang="ts">
  import type { RGB } from '@rapoo/device-sdk'
  import { ref, onUnmounted, computed, watch } from 'vue'
  import { rgbToHsv, hsvToRgb } from '@/utils/color'
  import { throttle } from '@/utils/optimize'

  const props = withDefaults(
    defineProps<{
      rgb?: RGB
    }>(),
    {
      rgb: () => ({ r: 255, g: 0, b: 0 }),
    },
  )

  const emit = defineEmits(['update:rgb', 'change'])

  // 缓存原始色相
  const rawHue = ref(0)
  // 是否正在拖动
  const isDragging = ref(false)

  // 初始化：从props.rgb计算初始色相
  const initHsv = rgbToHsv(props.rgb!)
  rawHue.value = initHsv.h

  // 监听外部RGB变化，同步更新rawHue（仅非拖动状态下）
  watch(
    () => props.rgb,
    (newRgb) => {
      if (isDragging.value) return // 拖动中不更新，避免冲突
      const newHsv = rgbToHsv(newRgb!)
      rawHue.value = newHsv.h // 同步外部RGB对应的色相
    },
    { deep: true }, // 监听RGB对象内部属性变化
  )

  // 计算HSV：h使用缓存的rawHue，s/v从RGB转换（保证饱和度/亮度和RGB一致）
  const hsv = computed(() => {
    const baseHsv = rgbToHsv(props.rgb!)
    return {
      h: rawHue.value,
      s: baseHsv.s < 0.1 ? 100 : baseHsv.s,
      v: baseHsv.v < 0.1 ? 100 : baseHsv.v,
    }
  })

  // 滑块位置百分比：基于rawHue计算，永不归一化为0%
  const huePercentage = computed(() => {
    const clampedHue = Math.max(0, Math.min(rawHue.value, 360))
    return (clampedHue / 360) * 100
  })

  // 精准计算色相（保留1位小数，避免微小区间丢失）
  const calculateHue = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect()
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    const percentage = x / rect.width
    let newHue = parseFloat((percentage * 360).toFixed(1))
    // 边界处理：360°强制设为359°（避免rgbToHsv归一化为0°）
    if (newHue >= 360) newHue = 359
    return newHue
  }

  // 更新RGB并同步rawHue
  const updateRgbByHue = (newHue: number) => {
    rawHue.value = newHue // 更新缓存色相
    const newHsv = { ...hsv.value, h: newHue }
    const newRgb = hsvToRgb(newHsv)
    emit('update:rgb', newRgb)
  }

  let dragEl: HTMLElement | null = null
  const onMove = throttle((e: MouseEvent | TouchEvent) => {
    if (!isDragging.value || !dragEl) return
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
    const newHue = calculateHue(clientX, dragEl)
    updateRgbByHue(newHue)
  }, 16)

  const cleanup = (isUnmounted: boolean = false) => {
    isDragging.value = false
    if (!isUnmounted) {
      emit('change', props.rgb)
    }
    dragEl = null
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onEnd)
    document.removeEventListener('touchmove', onMove)
    document.removeEventListener('touchend', onEnd)
  }

  const onEnd = () => {
    cleanup(false)
  }

  const startDrag = (e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    isDragging.value = true // 标记为拖动中，暂停外部RGB同步
    dragEl = e.currentTarget as HTMLElement

    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
    const newHue = calculateHue(clientX, dragEl)
    updateRgbByHue(newHue)

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onEnd)
    document.addEventListener('touchmove', onMove, { passive: false })
    document.addEventListener('touchend', onEnd)
  }

  onUnmounted(() => {
    cleanup(true)
  })
</script>
<style scoped>
  .gradient-mask {
    top: 0;
    transform: translateY(-100%);
    background: linear-gradient(
      to right,
      hsla(0, 100%, 50%, 0),
      hsl(60, 100%, 50%, 0.5),
      hsl(120, 100%, 50%, 0.7),
      hsl(180, 100%, 50%, 1),
      hsl(240, 100%, 50%, 0.7),
      hsl(300, 100%, 50%, 0.5),
      hsl(359, 100%, 50%, 0)
    );
    -webkit-mask: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 15%,
      rgba(0, 0, 0, 0) 25%,
      rgba(0, 0, 0, 0) 40%,
      rgba(0, 0, 0, 0) 60%,
      rgba(0, 0, 0, 0.1) 80%,
      rgba(0, 0, 0, 0.2) 100%
    );
    mask: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 15%,
      rgba(0, 0, 0, 0) 25%,
      rgba(0, 0, 0, 0) 40%,
      rgba(0, 0, 0, 0) 60%,
      rgba(0, 0, 0, 0.1) 80%,
      rgba(0, 0, 0, 0.2) 100%
    );
  }
</style>
